import { create } from 'zustand';

import type { TPixiMode } from "~/components/PixiBackground";
import { ISavedUploadedFile, savedStore } from "./saved";
import { ICovid, ILogin, TService } from "~/interfaces";

interface IGlobalStore {
	global: {
		isWinter: boolean;
		isHalloween: boolean;
		logAuthLoading: boolean;
		loginData: ILogin | null;
		pixiMode: TPixiMode;
		hidePage: boolean;
		service: TService;
		christmas: boolean;
		messengerNews: boolean;
		newGallery: boolean;
		galleryFilterNewest: boolean;
		uploadedFileNews: boolean;
		covidData: ICovid | null;
	};
	setLogin: (loginData: ILogin | null) => void;
	setPixiMode: (pixiMode: TPixiMode) => void;
	setHidePage: (hidePage: boolean) => void;
	setService: (service: TService) => void;
	setChristmas: (christmas: boolean) => void;
	setIsWinter: (isWinter: boolean) => void;
	setIsHalloween: (isHalloween: boolean) => void;
	setKokokyka: (kokokyka: number) => void;
	setMessengerNews: (messengerNews: boolean) => void;
	setLogAuthLoading: (logAuthLoading: boolean) => void;
	setNewGallery: (newGallery: boolean) => void;
	setGalleryFilterNewest: (galleryFilterNewest: boolean) => void;
	setUploadedFileNews: (uploadedFileNews: boolean) => void;
	setCovidData: (covidData: ICovid) => void;
}

/* eslint-disable no-magic-numbers */
// zimni rezim: od 1. adventni nedele do 31. ledna nasledujiciho roku
function isWinterSeason(date = new Date()) {
	const year = date.getFullYear();

	// leden
	if (date.getMonth() === 0) {
		return true;
	}

	// 1. adventni nedele je mezi 27. 11. a 3. 12. (prvni nedele od 27. 11.)
	const nov27 = new Date(year, 10, 27);
	const firstAdvent = new Date(year, 10, 27 + ((7 - nov27.getDay()) % 7));

	return date >= firstAdvent;
}

// halloweensky rezim: od 30. rijna 14 dni (do 12. listopadu vcetne)
function isHalloweenSeason(date = new Date()) {
	const start = new Date(date.getFullYear(), 9, 30);
	const end = new Date(date.getFullYear(), 9, 30 + 14);

	return date >= start && date < end;
}
/* eslint-enable no-magic-numbers */

// simulace pres url: ?winter=1 zapne, ?winter=0 vypne zimni rezim
function getIsWinter() {
	const winter = new URLSearchParams(location.search).get("winter");

	if (winter === "1" || winter === "0") {
		return winter === "1";
	}

	return isWinterSeason();
}

// simulace pres url: ?halloween=1 zapne, ?halloween=0 vypne halloweensky rezim
function getIsHalloween() {
	const halloween = new URLSearchParams(location.search).get("halloween");

	if (halloween === "1" || halloween === "0") {
		return halloween === "1";
	}

	return isHalloweenSeason();
}

function hasMessengerNews(loginData: ILogin | null, lastId: number) {
	if (!loginData?.isAdmin || loginData.messengerInd === 0) {
		return false;
	}

	return lastId > 0 && lastId !== loginData.messengerInd;
}

function hasUploadedFileNews(loginData: ILogin | null, lastFile: ISavedUploadedFile) {
	const loginFile = loginData?.uploadedFile;

	if (!loginData?.isAdmin || !loginFile?.file) {
		return false;
	}

	// datum "YYYY-MM-DD HH:mm" jde porovnat jako retezec; bez data jen stary zaznam s nazvem souboru
	if (lastFile.date) {
		return loginFile.date > lastFile.date;
	}

	return lastFile.file !== loginFile.file;
}

export const globalStore = create<IGlobalStore>(set => ({
	global: {
		isWinter: getIsWinter(),
		isHalloween: getIsHalloween(),
		logAuthLoading: false,
		loginData: null,
		pixiMode: "none",
		hidePage: false,
		service: "makuderovi" as TService,
		christmas: false,
		messengerNews: false,
		uploadedFileNews: false,
		newGallery: false,
		galleryFilterNewest: false,
		covidData: null,
	},
	setLogin: loginData => {
		const { saved } = savedStore.getState();

		return set(state => ({
			global: {
				...state.global,
				loginData,
				messengerNews: hasMessengerNews(loginData, saved.messengerLastId),
				uploadedFileNews: hasUploadedFileNews(loginData, saved.uploadedFile),
				logAuthLoading: false,
			},
		}));
	},
	setPixiMode: pixiMode => set(state => ({
		global: {
			...state.global,
			pixiMode,
		},
	})),
	setHidePage: hidePage => set(state => ({
		global: {
			...state.global,
			hidePage,
		},
	})),
	setService: service => set(state => ({
		global: {
			...state.global,
			service,
		},
	})),
	setChristmas: christmas => set(state => ({
		global: {
			...state.global,
			christmas,
		},
	})),
	setIsWinter: isWinter => set(state => ({
		global: {
			...state.global,
			isWinter,
		},
	})),
	setIsHalloween: isHalloween => set(state => ({
		global: {
			...state.global,
			isHalloween,
		},
	})),
	setKokokyka: kokokyka => set(state => ({
		global: {
			...state.global,
			loginData: {
				...state.global.loginData,
				kokokyka,
			},
		},
	})),
	setMessengerNews: messengerNews => set(state => ({
		global: {
			...state.global,
			messengerNews,
		},
	})),
	setUploadedFileNews: uploadedFileNews => set(state => ({
		global: {
			...state.global,
			uploadedFileNews,
		},
	})),
	setLogAuthLoading: logAuthLoading => set(state => ({
		global: {
			...state.global,
			logAuthLoading,
		},
	})),
	setNewGallery: newGallery => set(state => ({
		global: {
			...state.global,
			newGallery,
		},
	})),
	setGalleryFilterNewest: galleryFilterNewest => set(state => ({
		global: {
			...state.global,
			galleryFilterNewest,
		},
	})),
	setCovidData: covidData => set(state => ({
		global: {
			...state.global,
			covidData,
		},
	})),
}));

// shlednute nahrane soubory (i v jine zalozce) -> prepocitame priznak
savedStore.subscribe((state, prevState) => {
	const { uploadedFile } = state.saved;
	const prevFile = prevState.saved.uploadedFile;

	if (uploadedFile.file === prevFile.file && uploadedFile.date === prevFile.date) {
		return;
	}

	const { global, setUploadedFileNews } = globalStore.getState();

	setUploadedFileNews(hasUploadedFileNews(global.loginData, uploadedFile));
});
